import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { CpuChipIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

interface ModelAutomationSubmenuProps {
  collapsed: boolean;
  onMobileClose?: () => void;
}

const items = [
  { path: '/model-automation/dashboard', label: 'Dashboard' },
  { path: '/model-automation/jobs', label: 'Jobs' },
  { path: '/model-automation/booking-runs', label: 'Booking Runs' },
  { path: '/model-automation/portfolio-runs', label: 'Nightly Portfolios' },
  { path: '/model-automation/throughput', label: 'Throughput' },
  { path: '/model-automation/trace', label: 'Trace' },
  { path: '/model-automation/retry', label: 'Manual Retry' },
  { path: '/model-automation/bind-likelihood', label: 'Bind Likelihood' },
  { path: '/model-automation/rms-intelligence', label: 'RMS Intelligence' },
];

export const ModelAutomationSubmenu: React.FC<ModelAutomationSubmenuProps> = ({ collapsed, onMobileClose }) => {
  const location = useLocation();
  const isModelRoute = location.pathname.startsWith('/model-automation');
  const [isOpen, setIsOpen] = useState(isModelRoute);

  useEffect(() => {
    if (isModelRoute) setIsOpen(true);
    else setIsOpen(false);
  }, [isModelRoute]);

  if (collapsed) {
    return (
      <div className="relative">
        <NavLink
          to="/model-automation/jobs"
          onClick={onMobileClose}
          className={({ isActive }) =>
            clsx(
              'group flex items-center justify-center px-0 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200 w-full',
              (isActive || isModelRoute) && 'bg-white/10 text-white border-l-4 border-white/60'
            )
          }
          title="Model Automation"
        >
          <CpuChipIcon className="w-5 h-5 mx-auto transition-transform duration-200 group-hover:translate-x-0.5" />
        </NavLink>
      </div>
    );
  }

  return (
    <div>
      <div className={clsx('group flex items-center w-full text-white/80 transition-all duration-200', isModelRoute && 'bg-white/10 text-white')}>
        <NavLink
          to="/model-automation/jobs"
          onClick={onMobileClose}
          className="flex min-w-0 flex-1 items-center px-4 py-3 hover:bg-white/10 hover:text-white"
        >
          <CpuChipIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
          <span className="ml-3">Model Automation</span>
        </NavLink>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="px-4 py-3 hover:bg-white/10 hover:text-white"
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label="Toggle Model Automation submenu"
        >
          <ChevronDownIcon className={clsx('w-4 h-4 transition-transform duration-200', isOpen && 'rotate-180')} />
        </button>
      </div>

      {isOpen && (
        <div className="mx-4 mb-2 bg-black/20 rounded-lg border border-white/10 overflow-hidden">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onMobileClose}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-4 py-2.5 text-white/70 hover:bg-white/5 hover:text-white transition-all duration-200',
                  'border-l-2 border-transparent hover:border-white/30',
                  isActive && 'bg-white/10 text-white border-l-2 border-white/60'
                )
              }
            >
              <span className="text-sm">{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};
